import { MAX_WEB_REGIONS, } from "./types/signage-template";
export const DEFAULT_THEME = {
    background: "#101014",
    ink: "#FFFFFF",
    accent: "#C6A667",
    font: '"Inter Tight", system-ui, sans-serif',
};
const SIZES = {
    title: 128,
    body: 64,
    small: 40,
    panel: 0,
    accent: 0,
};
export function styleForRole(role, theme) {
    switch (role) {
        case "title":
            return {
                color: theme.ink,
                fontSize: SIZES.title,
                fontWeight: 700,
                fontFamily: theme.font,
                lineHeight: 1.05,
                verticalAlign: "middle",
            };
        case "body":
            return {
                color: theme.ink,
                fontSize: SIZES.body,
                fontWeight: 500,
                fontFamily: theme.font,
                lineHeight: 1.25,
                verticalAlign: "middle",
            };
        case "small":
            return {
                color: theme.ink,
                fontSize: SIZES.small,
                fontWeight: 500,
                fontFamily: theme.font,
                lineHeight: 1.3,
                verticalAlign: "middle",
            };
        case "accent":
            return { background: theme.accent };
        case "panel":
            return { background: "rgba(0,0,0,0.55)" };
    }
}
export function applyTheme(regions, theme) {
    return regions.map((region) => region.role
        ? {
            ...region,
            style: { ...region.style, ...styleForRole(region.role, theme) },
        }
        : region);
}
function defaultLabel(region) {
    switch (region.kind) {
        case "image":
            return "Picture";
        case "qr":
            return "Link";
        case "list":
            return "The list";
        case "slideshow":
            return "Pictures";
        case "web":
            return region.source === "instagram"
                ? "Elfsight Instagram link"
                : region.source === "vilmer"
                    ? "VilMer board address"
                    : "Web address";
        default:
            return region.role === "title" ? "Headline" : "Text";
    }
}
export function fieldsFor(regions, labels) {
    return regions
        .filter((r) => r.binding.kind === "field")
        .map((r) => {
        var _a;
        const binding = r.binding;
        return {
            ...(binding.unitEditable ? { unitEditable: true } : {}),
            ...(r.kind === "list" && r.table
                ? { help: "One row per line, with | between the columns." }
                : {}),
            ...(r.kind === "web" && r.source === "instagram"
                ? {
                    help: "The Elfsight widget's share link (Install → Share Link), or its install code.",
                }
                : {}),
            key: binding.field,
            label: ((_a = labels[binding.field]) === null || _a === void 0 ? void 0 : _a.trim()) || defaultLabel(r),
            type: r.kind === "image"
                ? "image"
                : r.kind === "slideshow"
                    ? "images"
                    : r.kind === "qr" || r.kind === "web"
                        ? "url"
                        : r.kind === "list"
                            ? "rows"
                            : r.role === "body"
                                ? "multiline"
                                : "text",
        };
    });
}
const W = 1920;
const H = 1080;
function r(id, kind, rect, role, binding, extra = {}) {
    return {
        id,
        kind,
        rect,
        binding,
        ...(role ? { role } : {}),
        style: {
            ...(role ? styleForRole(role, DEFAULT_THEME) : {}),
            ...extra,
        },
    };
}
export const TEMPLATE_PRESETS = [
    {
        id: "photo-headline",
        name: "Photo with a headline",
        description: "A full-bleed picture with words across the bottom.",
        canvas: { width: W, height: H },
        regions: [
            r("bg", "image", { x: 0, y: 0, w: W, h: H }, undefined, {
                kind: "field",
                field: "picture",
            }, { fit: "cover" }),
            r("scrim", "shape", { x: 0, y: 620, w: W, h: 460 }, "panel", {
                kind: "fixed",
                value: "",
            }),
            r("title", "text", { x: 120, y: 700, w: 1400, h: 180 }, "title", {
                kind: "field",
                field: "headline",
            }),
            r("sub", "text", { x: 120, y: 890, w: 1400, h: 100 }, "small", {
                kind: "field",
                field: "detail",
            }),
        ],
    },
    {
        id: "split",
        name: "Picture beside words",
        description: "Half picture, half message. Good for an offer.",
        canvas: { width: W, height: H },
        regions: [
            r("pic", "image", { x: 0, y: 0, w: 960, h: H }, undefined, {
                kind: "field",
                field: "picture",
            }, { fit: "cover" }),
            r("title", "text", { x: 1040, y: 300, w: 780, h: 240 }, "title", {
                kind: "field",
                field: "headline",
            }),
            r("body", "text", { x: 1040, y: 560, w: 780, h: 260 }, "body", {
                kind: "field",
                field: "detail",
            }),
        ],
    },
    {
        id: "bar-headline",
        name: "Brand bar and a message",
        description: "A coloured band at the top, your words underneath.",
        canvas: { width: W, height: H },
        regions: [
            r("bar", "shape", { x: 0, y: 0, w: W, h: 180 }, "accent", {
                kind: "fixed",
                value: "",
            }),
            r("logo", "image", { x: 80, y: 40, w: 300, h: 100 }, undefined, {
                kind: "fixed",
                value: "",
            }, { fit: "contain" }),
            r("title", "text", { x: 120, y: 380, w: 1680, h: 260 }, "title", {
                kind: "field",
                field: "headline",
            }),
            r("body", "text", { x: 120, y: 660, w: 1680, h: 240 }, "body", {
                kind: "field",
                field: "detail",
            }),
        ],
    },
    {
        id: "scan-me",
        name: "Message with a QR code",
        description: "Words on the left, something to scan on the right.",
        canvas: { width: W, height: H },
        regions: [
            r("title", "text", { x: 120, y: 300, w: 1100, h: 240 }, "title", {
                kind: "field",
                field: "headline",
            }),
            r("body", "text", { x: 120, y: 560, w: 1100, h: 220 }, "body", {
                kind: "field",
                field: "detail",
            }),
            r("qr", "qr", { x: 1400, y: 380, w: 380, h: 380 }, undefined, {
                kind: "field",
                field: "link",
            }),
        ],
    },
    {
        id: "whats-on",
        name: "What's on today",
        description: "A heading and a list of times — classes, activities, opening hours.",
        canvas: { width: W, height: H },
        regions: [
            r("bar", "shape", { x: 0, y: 0, w: W, h: 160 }, "accent", {
                kind: "fixed",
                value: "",
            }),
            r("title", "text", { x: 100, y: 30, w: 1200, h: 100 }, "title", {
                kind: "fixed",
                value: "Today",
            }),
            r("clock", "clock", { x: 1440, y: 30, w: 380, h: 100 }, "title", {
                kind: "fixed",
                value: "",
            }, { align: "right" }),
            r("rows", "list", { x: 100, y: 230, w: 1720, h: 780 }, "body", {
                kind: "field",
                field: "rows",
            }),
        ],
    },
    {
        id: "menu",
        name: "Menu",
        description: "A picture on one side, the day's dishes on the other.",
        canvas: { width: W, height: H },
        regions: [
            r("pic", "image", { x: 0, y: 0, w: 780, h: H }, undefined, {
                kind: "field",
                field: "picture",
            }, { fit: "cover" }),
            r("title", "text", { x: 860, y: 120, w: 960, h: 140 }, "title", {
                kind: "field",
                field: "headline",
            }),
            r("rows", "list", { x: 860, y: 300, w: 960, h: 660 }, "body", {
                kind: "field",
                field: "rows",
            }),
        ],
    },
    {
        id: "notice",
        name: "Plain notice",
        description: "Just words, big. For an announcement.",
        canvas: { width: W, height: H },
        regions: [
            r("title", "text", { x: 160, y: 340, w: 1600, h: 260 }, "title", {
                kind: "field",
                field: "headline",
            }, { align: "center" }),
            r("body", "text", { x: 160, y: 620, w: 1600, h: 220 }, "body", {
                kind: "field",
                field: "detail",
            }, { align: "center" }),
            r("clock", "clock", { x: 1480, y: 60, w: 360, h: 110 }, "small", {
                kind: "fixed",
                value: "",
            }, { align: "right" }),
        ],
    },
];
export const SIGNAGE_WIDGETS = [
    { id: "heading", label: "Heading", hint: "Big words" },
    { id: "text", label: "Text", hint: "A sentence or two" },
    { id: "image", label: "Image", hint: "From the media library" },
    {
        id: "slideshow",
        label: "Image rotation",
        hint: "Several pictures, one after another",
    },
    { id: "webpage", label: "Webpage", hint: "Any web address, in a box" },
    { id: "instagram", label: "Instagram", hint: "A feed, via Elfsight" },
    { id: "vilmer", label: "VilMer", hint: "Today's activities" },
    { id: "list", label: "List", hint: "Times and words: menu, schedule" },
    { id: "qr", label: "QR code", hint: "Something to scan" },
    { id: "clock", label: "Clock", hint: "The local time" },
    { id: "shape", label: "Colour block", hint: "A band or a backdrop" },
];
export const BLANK_PRESET_ID = "blank";
export function canAddWebRegion(regions) {
    return regions.filter((r) => r.kind === "web").length < MAX_WEB_REGIONS;
}
const WIDGET_SAMPLE_ROWS = [
    "08:00 | Breakfast",
    "10:30 | Coffee and a walk",
    "12:00 | Lunch",
].join("\n");
export function newWidgetRegion(widget, canvas, theme, id) {
    const k = Math.min(canvas.width / W, canvas.height / H);
    const box = (w, h) => {
        const bw = Math.round(Math.min(canvas.width, w * k));
        const bh = Math.round(Math.min(canvas.height, h * k));
        return {
            x: Math.round((canvas.width - bw) / 2 / 20) * 20,
            y: Math.round((canvas.height - bh) / 2 / 20) * 20,
            w: bw,
            h: bh,
        };
    };
    const fixed = (value) => ({ kind: "fixed", value });
    const styled = (role, extra = {}) => ({
        ...(role ? { role } : {}),
        style: { ...(role ? styleForRole(role, theme) : {}), ...extra },
    });
    const web = (source) => ({
        id,
        kind: "web",
        rect: box(960, 720),
        binding: fixed(""),
        source,
        style: { zoom: 1 },
    });
    switch (widget) {
        case "heading":
            return {
                id,
                kind: "text",
                rect: box(1200, 180),
                binding: fixed("Heading"),
                ...styled("title"),
            };
        case "text":
            return {
                id,
                kind: "text",
                rect: box(1000, 240),
                binding: fixed("Write something here."),
                ...styled("body"),
            };
        case "image":
            return {
                id,
                kind: "image",
                rect: box(800, 600),
                binding: fixed(""),
                style: { fit: "cover" },
            };
        case "slideshow":
            return {
                id,
                kind: "slideshow",
                rect: box(800, 600),
                binding: fixed(""),
                intervalSeconds: 8,
                style: { fit: "cover" },
            };
        case "webpage":
            return web("page");
        case "instagram":
            return { ...web("instagram"), rect: box(640, 900) };
        case "vilmer":
            return web("vilmer");
        case "list":
            return {
                id,
                kind: "list",
                rect: box(1000, 500),
                binding: fixed(WIDGET_SAMPLE_ROWS),
                ...styled("body"),
            };
        case "qr":
            return {
                id,
                kind: "qr",
                rect: box(360, 360),
                binding: fixed(""),
            };
        case "clock":
            return {
                id,
                kind: "clock",
                rect: box(400, 120),
                binding: fixed(""),
                ...styled("title", { align: "right" }),
            };
        case "shape":
            return {
                id,
                kind: "shape",
                rect: box(W, 180),
                binding: fixed(""),
                ...styled("accent"),
            };
    }
}
